﻿<script>
	import { tick } from "svelte";
	import model from "../../stores/modelStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, yesNoUnsureOptions } from "../../config";
	import { isValid } from "../../validation";
	import lookups from "../../stores/lookupStore";

	import AddButton from "../../components/buttons/AddButton.svelte";
	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import Select from "../../components/Select.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import SocialMediaDetails from "./SocialMediaDetails.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}
	let buttons = null;

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {

			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	const initialise = () => {
		if (!$model.claimDetails) {
			$model.claimDetails = {
				treatmentSupportServices: [],
				availableRecordTypes: []
			};
		}

		$model.claimDetails.treatmentSupportServices ??= [];
		$model.claimDetails.availableRecordTypes ??= [];
		$model.claimDetails.careProviders ??= [];

		if ($model.claimDetails.careProviders.length === 0) {
			$model.claimDetails.careProviders = [""];
		}
	};

	initialise();

	const addCareProvider = () => {
		$model.claimDetails.careProviders = [
			...($model.claimDetails.careProviders ?? []),
			""
		];
	};

	const removeCareProvider = (index) => {
		$model.claimDetails.careProviders =
			$model.claimDetails.careProviders.filter((_, i) => i !== index);
	};

	const handleTreatmentSupportServicesChange = async (event) => {
		const options = $lookups?.treatmentSupportServices ?? [];
		const noOption = options.find(option => option.label === "No");
		const clickedValue = event.target.value;

		if (!event.target.checked || !noOption) return;	// If unchecking or the "No" option is not found, do nothing.

		const selectedNo =
			String(clickedValue) === String(noOption.value);

		await tick();	// wait for the CheckboxButtonList to update before continuing, due to bind:group in CheckboxButtonList.svelte

		if (selectedNo) {
			$model.claimDetails.treatmentSupportServices = [noOption.value];
			$model.claimDetails.treatmentSupportServicesOther = "";
			$model.claimDetails.startTreatmentDate = null;
			return;
		}

		const clickedOption = options.find(
			option => String(option.value) === String(clickedValue)
		);
		if (!clickedOption) return;

		const selected = ($model.claimDetails.treatmentSupportServices ?? [])
			.filter(value => String(value) !== String(noOption.value));

		if (!selected.some(value => String(value) === String(clickedOption.value))) {
			selected.push(clickedOption.value);
		}

		$model.claimDetails.treatmentSupportServices = selected;
	};

	const handleAvailableRecordTypesChange = async (event) => {
		const options = $lookups?.availableRecordTypes ?? [];
		const noneOption = options.find(option => option.label === "None");
		const clickedValue = event.target.value;

		if (!event.target.checked || !noneOption) return;

		const selectedNone =
			String(clickedValue) === String(noneOption.value);

		await tick();

		if (selectedNone) {
			$model.claimDetails.availableRecordTypes = [noneOption.value];
			return;
		}

		const clickedOption = options.find(
			option => String(option.value) === String(clickedValue)
		);
		if (!clickedOption) return;

		const selected = ($model.claimDetails.availableRecordTypes ?? [])
			.filter(value => String(value) !== String(noneOption.value));

		if (!selected.some(value => String(value) === String(clickedOption.value))) {
			selected.push(clickedOption.value);
		}

		$model.claimDetails.availableRecordTypes = selected;
	};

	// For treatment support services, check if "Other" is selected to determine 
	// whether to show the text area for additional details.
	$: otherTreatmentSupportServices = ($lookups?.treatmentSupportServices ?? [])
		.find(condition => condition.label === "Other");

	$: showTreatmentSupportServicesOther =
		!!otherTreatmentSupportServices &&
		($model.claimDetails?.treatmentSupportServices ?? []).some(
			selected => String(selected) === String(otherTreatmentSupportServices.value)
		);

	$: noTreatmentSupportServicesOption = ($lookups?.treatmentSupportServices ?? [])
		.find(option => option.label === "No");

	$: hasNoTreatmentSupportServices =
		!!noTreatmentSupportServicesOption &&
		($model.claimDetails?.treatmentSupportServices ?? []).some(
			selected => String(selected) === String(noTreatmentSupportServicesOption.value)
		);


	$: if (
		$model.claimDetails?.continuingTreatment === "Yes" &&
		$model.claimDetails?.endTreatmentDate != null
	) {
		$model.claimDetails.endTreatmentDate = null;
	}
</script>

<PageSection>
	<Form {disabled} id="form-claim-details-treatment" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn>
					<CheckboxButtonList
						name="treatmentSupportServices"
						label="Did you receive treatment or support?"
						sublabel="Tick all that apply"
						bind:selected={$model.claimDetails.treatmentSupportServices}
						optionList={$lookups.treatmentSupportServices}
						on:change={handleTreatmentSupportServicesChange}
						isVertical={false}
						validator="checkboxRequired"
						validatorValue="1"
						validatorMessage="At least one selection is required"
						validatorTarget=".culture-error"
						labelProperty="label"
						valueProperty="value"
						{disabled}
					/>
				</FormColumn>
			</FormRow>
			{#if showTreatmentSupportServicesOther}
				<FormRow>
					<FormColumn>
						<TextArea
							{disabled}
							label="Please specify?"
							bind:value={$model.claimDetails.treatmentSupportServicesOther}
							id="treatmentSupportServicesOther" />
					</FormColumn>
				</FormRow>
			{/if}

			{#if !hasNoTreatmentSupportServices}
				<FormRow>
					<FormColumn cols="6">
						<DatePicker
							{disabled}
							required
							end={new Date()}
							name="startTreatmentDate"
							id="startTreatmentDate"
							label="When did you start receiving treatment? Approximate date is okay"
							openOnClass="calendar-icon"
							bind:formattedSelected={$model.claimDetails.startTreatmentDate} />
					</FormColumn>
				</FormRow>
			{/if}
			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						label="Are you continuing to receive treatment?"
						bind:value={$model.claimDetails.continuingTreatment}
						name="continuingTreatment"></YesNoGroup>
				</FormColumn>
			</FormRow>
			{#if $model.claimDetails.continuingTreatment === "No"}
				<FormRow>
					<FormColumn cols="6">
						<DatePicker
							{disabled}
							required
							end={new Date()}
							name="endTreatmentDate"
							id="endTreatmentDate"
							label="When did you cease treatment?"
							openOnClass="calendar-icon"
							bind:formattedSelected={$model.claimDetails.endTreatmentDate} />
					</FormColumn>
				</FormRow>
			{/if}
			<FormRow>
				<FormColumn>
					<Select
						required
						{disabled}
						label="Estimate the cost of your treatments to date."
						bind:value={$model.claimDetails.estimatedTreatmentCostId}
						optionList={$lookups.estimatedTreatmentCosts}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>
			<!-- <FormRow>
				<FormColumn>
					<TextArea
						{disabled}
						label="List the names of your care providers"
						bind:value={$model.claimDetails.careProviders}
						autoExpand={true}
						id="careProviders" />
				</FormColumn>
			</FormRow> -->

			{#each $model.claimDetails.careProviders as provider, i}
				<FormRow class="d-flex align-items-end">
					<FormColumn cols="11">
							<TextInput
								id={`careProvider-${i}`}
								label={i === 0 ? "List the names of your care providers" : null}
								maxlength={255}
								disabled={disabled}
								bind:value={$model.claimDetails.careProviders[i]} />
					</FormColumn>
					<FormColumn cols="1" class="last mt-0">
						{#if $model.claimDetails.careProviders.length > 1 && !disabled}
							<div class="confirmation-hide">
								<button
									on:click|preventDefault={() => removeCareProvider(i)}
									class="delete"
									title="Remove"
									aria-label="Remove">
									<i class="fas fa-trash"></i>
								</button>
							</div>
						{/if}
					</FormColumn>
					
				</FormRow>
			{/each}

			<div class="confirmation-hide" style="margin-top: 20px;">
				{#if !disabled}
					<AddButton {disabled} on:add={addCareProvider}>Add another care provider</AddButton>
				{/if}
			</div>

			<FormRow>
				<FormColumn>
					<CheckboxButtonList
						name="availableRecordTypes"
						label="What records do you have?"
						sublabel="Tick all that apply"
						bind:selected={$model.claimDetails.availableRecordTypes}
						optionList={$lookups.availableRecordTypes}
						on:change={handleAvailableRecordTypesChange}
						isVertical={false}
						validator="checkboxRequired"
						validatorValue="1"
						validatorMessage="At least one selection is required"
						validatorTarget=".culture-error"
						labelProperty="label"
						valueProperty="value"
						{disabled}
					/>
				</FormColumn>
			</FormRow>
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

<style>
	.delete {
		border: none;
		outline: none;
		background-color: transparent;
		font-size: 1.1rem;
		font-weight: 700;
		color: var(--color-primary);
		margin-top: 10px;
	}
</style>